import BlockBase from '@components/dnd/base/blockBase';
import { ColumnComp } from '@components/trace/column/column.comp';
import { EditorMode } from '@components/trace/column/editorMode.column';
import { useAtom } from '@hooks/useAtom';
import EndControlsFB, {
  type SettingsConfig
} from '@library/formBuilder/endControls';
import { DateEditor } from '@library/tableEditor/editors/date.editor';
import { type DNDBlock, ResolvedVariableType } from '@stratumn/studio-shared';
import { DateWidget } from '@widgets/date/date.widget';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { ParamsList } from '@/routes/routes.types';

import { initialDateData } from './date.data';
import { DateSettingsModal } from './date.settings';
import { DateIcon } from './date.utils';

const DateTableEditorBlock = (block: DNDBlock) => {
  const actionId = useParams()[ParamsList.ActionId] as string;

  const atom = useAtom({
    type: 'Table_Editor_Date',
    sourceId: block.id,
    parentId: actionId,
    parentKind: 'table_editor',
    initialData: initialDateData,
    variableInfo: {
      resolvedType: ResolvedVariableType.Date
    }
  });

  if (!atom) {
    return null;
  }

  const getModalSettings = (): SettingsConfig => ({
    title: atom.data.title || 'Date Column Title',
    content: <DateSettingsModal atom={atom} />
  });

  const getEndControls = () => (
    <EndControlsFB
      dataItem={atom}
      dndBlock={block}
      hasRequired
      settings={getModalSettings()}
    />
  );

  return (
    <BlockBase
      dataItem={atom}
      dndBlock={block}
      endControls={getEndControls()}
      icon={DateIcon}
      title={atom.data.title}
    >
      <ColumnComp
        atomId={atom.id}
        isDisplayedByDefault={atom.data.isDisplayedByDefault}
        isfixedColumn={atom.data.isfixedColumn}
        onDisplayByDefaultChange={v => (atom.data.isDisplayedByDefault = v)}
        onFixedColumnChange={v => (atom.data.isfixedColumn = v)}
        onTitleChange={v => (atom.data.title = v)}
        onWidthChange={v => (atom.data.width = v)}
        title={atom.data.title}
        width={atom.data.width}
      />
      <EditorMode
        atomId={atom.id}
        canSelectEditorType={false}
        isEditMode={atom.data.isEditMode}
        onIsEditModeChange={v => (atom.data.isEditMode = v)}
      />
      {atom.data.isEditMode ? (
        <DateEditor atom={atom} />
      ) : (
        <DateWidget
          atomId={atom.id}
          format={atom.data.cell.view.format}
          onFormatChange={format => (atom.data.cell.view.format = format)}
        />
      )}
    </BlockBase>
  );
};

export default observer(DateTableEditorBlock);
